import React, { useState, useEffect, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfo, getExemptionQuestions, stateOfResidenceQuestion, additionalWithholdingQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import * as S from '../../styles';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function KentuckyForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfo);
  const [basicInfoQuestions, setBasicInfoQuestions] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const exemptionForm = useRef();
  const exemptionQuestionsByVersion = getExemptionQuestions(version);
  const exemptionInitQ = getInitialQuestions(exemptionQuestionsByVersion);
  const [exemptionQuestionsWithAnswers, setExemptionQuestionsWithAnswers] = useState(exemptionInitQ);

  const getStateOfResidence = (questions) => {
    if (
      questions[0].answer.answer.exemption_from_withholding === 'fort_campbell_exemption'
      || data.exemption_from_withholding === 'fort_campbell_exemption'
    ) {
      return getInitialQuestions(stateOfResidenceQuestion);
    }
  };
  const [stateOfResidence, setStateOfResidence] = useState(
    data.exemption_from_withholding ? getStateOfResidence(exemptionQuestionsWithAnswers) : [],
  );

  const [stateOfResidenceError, setStateOfResidenceError] = useState(false);

  // render marital status options multiple choice question
  const renderStateOfResidenceQuestion = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={stateOfResidence}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stateOfResidence.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStateOfResidence(questions);
        }}
        ref={exemptionForm}
      />
      {stateOfResidenceError && isExemptFromWithholding && (
      <Flex justifyContent="flex-start" mt="-10px" mb="5px">
        <Text color="red">
          To qualify for the Fort Campbell Exemption Certificate, your state of residence may not be Kentucky.
        </Text>
      </Flex>
      )}
    </Flex>
  );

  const additionalWithholdingForm = useRef();
  const additionalWithholdingInitQ = getInitialQuestions(additionalWithholdingQuestion);
  const [additionalWithholding, setAdditionalWithholding] = useState(additionalWithholdingInitQ);

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfoQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfoQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfoQuestions(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const renderExemptionQuestionsForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={exemptionQuestionsWithAnswers}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionQuestionsWithAnswers.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionQuestionsWithAnswers(questions);
          setStateOfResidence(getStateOfResidence(questions));
        }}
        ref={exemptionForm}
      />
      {exemptionQuestionsWithAnswers[0].answer.answer.exemption_from_withholding === 'fort_campbell_exemption'
        ? renderStateOfResidenceQuestion(isReviewStep)
        : ''}
    </Flex>
  );

  const renderAdditionalWithholdingForm = (isReviewStep) => (
    <FormConsumer
      questions={additionalWithholding}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = additionalWithholding.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setAdditionalWithholding(questions);
      }}
      ref={additionalWithholdingForm}
    />
  );

  const handleValidation = () => {
    if (basicInfoForm && basicInfoForm.current
        && basicInfoForm.current.validate((error) => !!error)) {
      return true;
    }

    if (exemptionForm && exemptionForm.current
        && exemptionForm.current.validate((error) => !!error)) {
      return true;
    }

    if (additionalWithholdingForm && additionalWithholdingForm.current
        && additionalWithholdingForm.current.validate((error) => !!error)
    ) {
      return true;
    }

    /* Context: To qualify for Fort Campbell Exemption, you cannot be a resident of Kentucky */
    if (isExemptFromWithholding
      && exemptionQuestionsWithAnswers[0].answer.answer.exemption_from_withholding === 'fort_campbell_exemption'
      && stateOfResidence !== undefined
      && stateOfResidence[0].answer.answer.state_of_residence === 'KY') {
      setStateOfResidenceError(true);
      return true;
    }
    setStateOfResidenceError(false);

    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    const basicInfoData = basicInfoQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    // Initialise data
    const exemptionStatusData = { is_exempt: isExemptFromWithholding };
    let exemptionData = resetInitialQuestions(exemptionInitQ)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let stateOfResidenceData = resetInitialQuestions(stateOfResidenceQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let additionalWithholdingData = resetInitialQuestions(additionalWithholdingQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (isExemptFromWithholding) {
      exemptionData = exemptionQuestionsWithAnswers
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

      /* If exempted and selects Fort Campbell Exemption Certificate */
      if (exemptionQuestionsWithAnswers[0].answer.answer.exemption_from_withholding === 'fort_campbell_exemption') {
        stateOfResidenceData = stateOfResidence
          .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
      }
    } else {
      additionalWithholdingData = additionalWithholding
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }

    const payload = {
      ...basicInfoData,
      ...exemptionStatusData,
      ...exemptionData,
      ...stateOfResidenceData,
      ...additionalWithholdingData,
    };
    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderAdditionalWithholdingForm()}
          {isExemptFromWithholding && renderExemptionQuestionsForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderAdditionalWithholdingForm(true)}
        {isExemptFromWithholding && renderExemptionQuestionsForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  useEffect(() => {
    setExemptionQuestionsWithAnswers(exemptionInitQ);
  }, [version]);

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}
export default KentuckyForm;
